'use client';
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useRef, useState, useTransition } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Briefcase,
  Building2,
  ChevronDown,
  Home,
  Key,
  Landmark,
  LandPlot,
  Loader2,
  Map,
  MapPin,
  Maximize2,
  Search,
  SlidersHorizontal,
  Store,
  X,
} from 'lucide-react';
import { cn, formatPrice, PROPERTY_TYPE_GROUPS, PROPERTY_TYPE_LABEL_MAP } from '@/lib/utils';
import { EASE_PREMIUM } from '@/lib/motion';

const BROWSER_API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

const PROP_TYPE_ICONS: Record<string, React.ReactNode> = {
  apartment: <Building2 className="h-4 w-4" />,
  studio: <Building2 className="h-4 w-4" />,
  villa: <Home className="h-4 w-4" />,
  maison: <Home className="h-4 w-4" />,
  riad: <Landmark className="h-4 w-4" />,
  terrain_urbain: <LandPlot className="h-4 w-4" />,
  terrain_rural: <LandPlot className="h-4 w-4" />,
  terrain_agricole: <LandPlot className="h-4 w-4" />,
  commercial: <Store className="h-4 w-4" />,
  bureau: <Briefcase className="h-4 w-4" />,
};

function fmtBudget(v: string): string {
  const n = Number(v);
  if (!v || isNaN(n) || n === 0) return v;
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

const quickSearches = [
  { label: 'Casablanca', href: '/acheter?q=casablanca' },
  { label: 'Marrakech', href: '/acheter?q=marrakech' },
  { label: 'Villa Rabat', href: '/acheter?q=rabat&type=villa' },
  { label: 'Riads', href: '/acheter?type=riad' },
];



type ActiveField = 'location' | 'type' | 'superficie' | 'budget' | null;

interface HeroSearchProps {
  /** Render with light/glass styling on dark background (default) or as a solid card on a light page. */
  variant?: 'glass' | 'solid';
}

export function HeroSearch({ variant = 'glass' }: HeroSearchProps) {
  const router = useRouter();
  const [tab, setTab] = useState<'sale' | 'rent'>('sale');
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [minSurface, setMinSurface] = useState('');
  const [maxSurface, setMaxSurface] = useState('');
  const [minBudget, setMinBudget] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [isPending, startTransition] = useTransition();
  const [activeField, setActiveField] = useState<ActiveField>(null);

  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const [loadingSugg, setLoadingSugg] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const cardRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        setActiveField(null);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const fetchSugg = useCallback(
    async (query: string) => {
      if (query.length < 1) {
        setSuggestions([]);
        setCities([]);
        setFocusedIdx(-1);
        return;
      }
      setLoadingSugg(true);
      try {
        const [propResp, cityResp] = await Promise.all([
          fetch(
            `${BROWSER_API}/api/properties?status=active&q=${encodeURIComponent(query)}&transactionType=${tab}&limit=4`,
            { cache: 'no-store' },
          ),
          fetch(`${BROWSER_API}/api/cities/search?q=${encodeURIComponent(query)}`, {
            cache: 'no-store',
          }),
        ]);
        if (propResp.ok) {
          const d = await propResp.json();
          setSuggestions(d.data || []);
        }
        if (cityResp.ok) {
          const d = await cityResp.json();
          setCities(d.slice(0, 5));
        }
      } catch {
        /* ignore */
      } finally {
        setLoadingSugg(false);
      }
    },
    [tab],
  );

  useEffect(() => {
    if (q.length < 1) {
      setSuggestions([]);
      setCities([]);
      setFocusedIdx(-1);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSugg(q), 150);
    return () => clearTimeout(debounceRef.current);
  }, [q, fetchSugg]);

  const doSearch = () => {
    setActiveField(null);
    const base = tab === 'sale' ? '/acheter' : '/louer';
    const p = new URLSearchParams();
    if (q.trim()) p.set('q', q.trim());
    if (type) p.set('type', type);
    if (minSurface) p.set('minSurface', minSurface);
    if (maxSurface) p.set('maxSurface', maxSurface);
    if (minBudget) p.set('minPrice', minBudget);
    if (maxBudget) p.set('maxPrice', maxBudget);
    startTransition(() => router.push(`${base}${p.toString() ? '?' + p : ''}`));
  };

  const typeLabel = type ? (PROPERTY_TYPE_LABEL_MAP[type] || type) : 'Type de bien';
  const superficieLabel =
    minSurface && maxSurface ? `${minSurface} – ${maxSurface} m²`
      : minSurface ? `≥ ${minSurface} m²`
        : maxSurface ? `≤ ${maxSurface} m²`
          : 'Superficie';
  const budgetLabel =
    minBudget && maxBudget ? `${fmtBudget(minBudget)} – ${fmtBudget(maxBudget)} MAD`
      : minBudget ? `≥ ${fmtBudget(minBudget)} MAD`
        : maxBudget ? `≤ ${fmtBudget(maxBudget)} MAD`
          : 'Budget';

  const allSuggItems = [
    ...cities.map((c: any) => ({ kind: 'city' as const, data: c })),
    ...suggestions.map((p: any) => ({ kind: 'property' as const, data: p })),
  ];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIdx((prev) => Math.min(prev + 1, allSuggItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIdx((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      if (focusedIdx >= 0 && focusedIdx < allSuggItems.length) {
        const item = allSuggItems[focusedIdx];
        if (item.kind === 'city') {
          setQ(item.data.name);
          setActiveField(null);
          setFocusedIdx(-1);
        } else {
          setActiveField(null);
          startTransition(() => router.push(`/annonce/${item.data.slug}`));
        }
      } else {
        doSearch();
      }
    } else if (e.key === 'Escape') {
      setActiveField(null);
      setFocusedIdx(-1);
    }
  };

  const isOpen = (f: ActiveField) => activeField === f;
  const onDark = variant === 'glass';

  return (
    <div ref={cardRef} className="relative z-50 w-full md:max-w-3xl lg:max-w-4xl xl:max-w-5xl mx-auto">
      {/* Tabs */}
      <div
        role="tablist"
        aria-label="Type de transaction"
        className={cn(
          'relative inline-flex p-1 rounded-md mb-3',
          onDark ? 'bg-white/10 backdrop-blur-md' : 'bg-surface-cream',
        )}
      >
        <TabPill
          active={tab === 'sale'}
          onClick={() => {
            setTab('sale');
            setMinBudget('');
            setMaxBudget('');
          }}
          icon={<Home className="h-4 w-4" />}
          label="Acheter"
          onDark={onDark}
        />
        <TabPill
          active={tab === 'rent'}
          onClick={() => {
            setTab('rent');
            setMinBudget('');
            setMaxBudget('');
          }}
          icon={<Key className="h-4 w-4" />}
          label="Louer"
          onDark={onDark}
        />
      </div>

      {/* Glass / solid card */}
      <motion.div
        layout
        transition={{ duration: 0.4, ease: EASE_PREMIUM }}
        className={cn(
          'relative rounded-xl overflow-visible transition-shadow duration-400 ease-premium',
          onDark
            ? 'bg-surface/95 backdrop-blur-xl shadow-glass'
            : 'bg-surface border border-border-subtle shadow-card-hover',
          activeField ? 'shadow-glass' : '',
        )}
      >
        {/* Row 1 — Location + search (relative so suggestions dropdown spans full card width) */}
        <div className="relative flex items-stretch border-b border-border-subtle">
          <div
            className="relative flex-1 flex items-center gap-3 px-5 py-4 cursor-text"
            onClick={() => {
              setActiveField('location');
              inputRef.current?.focus();
            }}
          >
            <MapPin
              className={cn(
                'h-5 w-5 shrink-0 transition-colors',
                isOpen('location') || q ? 'text-accent' : 'text-ink-muted',
              )}
            />
            <div className="flex-1 min-w-0">
              <p className="eyebrow text-[10px] mb-0.5">Localisation</p>
              <input
                ref={inputRef}
                type="text"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setActiveField('location');
                }}
                onFocus={() => setActiveField('location')}
                onKeyDown={handleKeyDown}
                placeholder="Ville, quartier, adresse ou référence…"
                className="w-full text-[15px] text-ink placeholder:text-ink-muted/60 bg-transparent outline-none"
                aria-label="Rechercher par localisation"
                aria-autocomplete="list"
              />
            </div>
            {loadingSugg && <Loader2 className="h-4 w-4 text-ink-muted animate-spin" />}
            {q && !loadingSugg && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setQ('');
                  setSuggestions([]);
                }}
                className="p-1 rounded-full hover:bg-surface-cream text-ink-muted hover:text-ink transition-colors"
                aria-label="Effacer"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}

          </div>

          <div className="p-3 flex items-center">
            <button
              type="button"
              onClick={doSearch}
              disabled={isPending}
              className={cn(
                'inline-flex items-center gap-2 h-12 px-6 rounded-md font-semibold text-sm',
                'bg-cta text-cta-foreground shadow-sm transition-all duration-200',
                'hover:bg-cta-hover disabled:opacity-60',
              )}
            >
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              <span className="hidden sm:inline">{isPending ? 'Recherche…' : 'Rechercher'}</span>
            </button>
          </div>
        </div>

        {/* Location suggestions — anchored to full card width */}
        <AnimatePresence>
          {isOpen('location') && q.length >= 1 && (
            <SuggestionsDropdown
              cities={cities}
              suggestions={suggestions}
              query={q}
              loading={loadingSugg}
              focusedIdx={focusedIdx}
              setFocusedIdx={setFocusedIdx}
              onPickCity={(name) => {
                setQ(name);
                setActiveField(null);
                setFocusedIdx(-1);
              }}
              onPickProperty={(slug) => {
                setActiveField(null);
                startTransition(() => router.push(`/annonce/${slug}`));
              }}
              onSearchAll={doSearch}
              isRent={tab === 'rent'}
            />
          )}
        </AnimatePresence>

        {/* Row 2 — Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border-subtle">
          <TypePickerField
            open={isOpen('type')}
            onToggle={() => setActiveField(isOpen('type') ? null : 'type')}
            value={typeLabel}
            active={!!type}
            selected={type}
            onSelect={(v) => {
              setType(v);
              setActiveField(null);
            }}
            groups={PROPERTY_TYPE_GROUPS.filter(
              (g) => tab === 'sale' || g.available_for.includes('rent'),
            )}
          />
          <NumberRangeField
            open={isOpen('superficie')}
            onToggle={() => setActiveField(isOpen('superficie') ? null : 'superficie')}
            icon={<Maximize2 className="h-4 w-4" />}
            label="Superficie"
            value={superficieLabel}
            active={!!(minSurface || maxSurface)}
            minValue={minSurface}
            maxValue={maxSurface}
            onMinChange={setMinSurface}
            onMaxChange={setMaxSurface}
            minPlaceholder="Min"
            maxPlaceholder="Max"
            unit="m²"
            onClear={() => { setMinSurface(''); setMaxSurface(''); }}
          />
          <NumberRangeField
            open={isOpen('budget')}
            onToggle={() => setActiveField(isOpen('budget') ? null : 'budget')}
            icon={<SlidersHorizontal className="h-4 w-4" />}
            label="Budget"
            value={budgetLabel}
            active={!!(minBudget || maxBudget)}
            minValue={minBudget}
            maxValue={maxBudget}
            onMinChange={setMinBudget}
            onMaxChange={setMaxBudget}
            minPlaceholder="Min"
            maxPlaceholder="Max"
            unit="MAD"
            align="right"
            onClear={() => { setMinBudget(''); setMaxBudget(''); }}
          />
        </div>
      </motion.div>

      {/* Map CTA */}
      <div className="flex justify-center mt-4">
        <Link
          href="/carte"
          className={cn(
            'inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium border transition-colors duration-200',
            onDark
              ? 'border-white/30 text-surface-light hover:bg-white/10 hover:border-white/60'
              : 'border-cta/40 text-cta-hover hover:bg-cta/5 hover:border-cta',
          )}
        >
          <Map className="h-4 w-4" />
          Rechercher sur la carte
        </Link>
      </div>

      {/* Quick links */}
      <div className="flex flex-wrap justify-center items-center gap-2 mt-4">
        <span className={cn('text-xs mr-1', onDark ? 'text-surface-light/50' : 'text-ink-muted')}>
          Tendances :
        </span>
        {quickSearches.map((item) => (
          <Link
            key={item.label}
            href={item.href}
            className={cn(
              'text-xs px-3.5 py-1.5 rounded-sm border transition-colors duration-200',
              onDark
                ? 'border-white/20 text-surface-light/80 hover:bg-white/10 hover:border-white/40 hover:text-surface-light'
                : 'border-border-subtle text-ink-secondary hover:border-cta hover:text-cta-hover hover:bg-cta/5',
            )}
          >
            {item.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Sub-pieces
// ──────────────────────────────────────────────────────────────────────
function TabPill({
  active,
  onClick,
  icon,
  label,
  onDark,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  onDark: boolean;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'relative inline-flex items-center gap-2 px-5 py-2 rounded-sm text-sm font-medium transition-colors duration-200 z-10',
        active
          ? 'text-ink'
          : onDark
            ? 'text-surface-light/70 hover:text-surface-light'
            : 'text-ink-secondary hover:text-ink',
      )}
    >
      {active && (
        <motion.span
          layoutId="hero-tab-pill"
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          className="absolute inset-0 rounded-sm bg-surface shadow-sm -z-10"
        />
      )}
      {icon}
      {label}
    </button>
  );
}

function TypePickerField({
  open,
  onToggle,
  value,
  active,
  selected,
  onSelect,
  groups,
}: {
  open: boolean;
  onToggle: () => void;
  value: string;
  active: boolean;
  selected: string;
  onSelect: (v: string) => void;
  groups: Array<{ group_key: string; group_label: string; values: Array<{ key: string; label: string }> }>;
}) {
  // Initialise hovered group to whichever group contains the current selection,
  // or the first group when nothing is selected.
  const activeGroupKey =
    groups.find((g) => g.values.some((v) => v.key === selected))?.group_key ??
    groups[0]?.group_key ??
    null;

  // null = nothing hovered yet; only pre-select the group if user already has a value chosen
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);

  // When the panel opens: restore to the group of the current selection, or reset to null
  useEffect(() => {
    if (open) setHoveredGroup(selected ? activeGroupKey : null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentGroup = groups.find((g) => g.group_key === hoveredGroup);

  return (
    <div className="relative">
      {/* ── Trigger ── */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-3.5 text-left transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={cn('shrink-0 transition-colors', active || open ? 'text-accent' : 'text-ink-muted')}>
          <Building2 className="h-4 w-4" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="eyebrow text-[10px] mb-0.5">Type de bien</p>
          <p className={cn('text-sm font-medium truncate', active ? 'text-ink' : 'text-ink-muted')}>
            {value}
          </p>
        </div>
        {active ? (
          <button
            type="button"
            onMouseDown={(e) => { e.stopPropagation(); onSelect(''); }}
            className="p-1 rounded-full hover:bg-surface-cream text-ink-muted hover:text-ink transition-colors"
            aria-label="Effacer"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 text-ink-muted transition-transform duration-250 shrink-0',
              open && 'rotate-180',
            )}
          />
        )}
      </button>

      {/* ── Two-column fly-out ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: EASE_PREMIUM }}
            className="absolute left-0 top-full mt-2 flex bg-surface rounded-2xl shadow-card-hover border border-border-subtle z-50 overflow-hidden"
            style={{ minWidth: '19rem' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Left column — groups */}
            <div className="w-44 shrink-0 border-r border-border-subtle py-2">
              {/* "All types" row */}
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onSelect(''); }}
                className={cn(
                  'w-full text-left px-3.5 py-2 text-sm transition-colors',
                  !selected
                    ? 'bg-accent/10 text-ink font-medium'
                    : 'text-ink-secondary hover:bg-surface-cream hover:text-ink',
                )}
              >
                Tous les types
              </button>

              <div className="my-1.5 border-t border-border-subtle" />

              {groups.map((g) => {
                const isHovered = hoveredGroup === g.group_key;
                const hasSelection = g.values.some((v) => v.key === selected);
                return (
                  <button
                    key={g.group_key}
                    type="button"
                    onMouseEnter={() => setHoveredGroup(g.group_key)}
                    onFocus={() => setHoveredGroup(g.group_key)}
                    className={cn(
                      'w-full flex items-center justify-between px-3.5 py-2.5 text-sm transition-colors',
                      isHovered
                        ? 'bg-surface-cream text-ink'
                        : hasSelection
                          ? 'text-accent font-medium hover:bg-surface-cream'
                          : 'text-ink-secondary hover:bg-surface-cream hover:text-ink',
                    )}
                  >
                    <span className={cn('font-medium leading-snug text-left', hasSelection && !isHovered && 'text-accent')}>
                      {g.group_label}
                    </span>
                    <ChevronDown className="h-3 w-3 -rotate-90 text-ink-muted shrink-0 ml-1" />
                  </button>
                );
              })}
            </div>

            {/* Right column — values of hovered group */}
            <div className="flex-1 py-2 min-w-[10rem]">
              {currentGroup ? (
                <>
                  <p className="px-4 pt-1 pb-1.5 text-[10px] uppercase font-semibold tracking-widest text-ink-muted">
                    {currentGroup.group_label}
                  </p>
                  {currentGroup.values.map((v) => {
                    const isSel = selected === v.key;
                    return (
                      <button
                        key={v.key}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); onSelect(v.key); }}
                        className={cn(
                          'w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2',
                          isSel
                            ? 'bg-accent/10 text-ink font-medium'
                            : 'text-ink-secondary hover:bg-surface-cream hover:text-ink',
                        )}
                      >
                        <span
                          className={cn(
                            'w-1.5 h-1.5 rounded-full shrink-0 transition-colors',
                            isSel ? 'bg-accent' : 'bg-transparent',
                          )}
                        />
                        {v.label}
                      </button>
                    );
                  })}
                </>
              ) : (
                <p className="px-4 py-3 text-sm text-ink-muted">Survolez un groupe</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function NumberRangeField({
  open,
  onToggle,
  icon,
  label,
  value,
  active,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  minPlaceholder,
  maxPlaceholder,
  unit,
  onClear,
  align = 'left',
}: {
  open: boolean;
  onToggle: () => void;
  icon: React.ReactNode;
  label: string;
  value: string;
  active: boolean;
  minValue: string;
  maxValue: string;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
  minPlaceholder: string;
  maxPlaceholder: string;
  unit: string;
  onClear: () => void;
  align?: 'left' | 'right';
}) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-3.5 text-left transition-colors"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={cn('shrink-0 transition-colors', active || open ? 'text-accent' : 'text-ink-muted')}>
          {icon}
        </span>
        <div className="flex-1 min-w-0">
          <p className="eyebrow text-[10px] mb-0.5">{label}</p>
          <p className={cn('text-sm font-medium truncate', active ? 'text-ink' : 'text-ink-muted')}>
            {value}
          </p>
        </div>
        {active ? (
          <button
            type="button"
            onMouseDown={(e) => { e.stopPropagation(); onClear(); }}
            className="p-1 rounded-full hover:bg-surface-cream text-ink-muted hover:text-ink transition-colors"
            aria-label="Effacer"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 text-ink-muted transition-transform duration-250 shrink-0',
              open && 'rotate-180',
            )}
          />
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2, ease: EASE_PREMIUM }}
            className={cn(
              'absolute top-full mt-2 w-[calc(100vw-2rem)] sm:w-96 bg-surface rounded-2xl shadow-card-hover border border-border-subtle z-50 p-5',
              align === 'right' ? 'right-0' : 'left-0',
            )}
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:gap-3">
              <div className="flex-1">
                <label className="eyebrow text-[10px] mb-2 block">Min</label>
                <div className="relative">
                  <input
                    type="number"
                    value={minValue}
                    onChange={(e) => onMinChange(e.target.value)}
                    placeholder={minPlaceholder}
                    min="0"
                    className="w-full h-13 px-4 pr-10 rounded-xl border border-border bg-surface-cream text-base text-ink focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-ink-muted font-semibold pointer-events-none">
                    {unit}
                  </span>
                </div>
              </div>
              <span className="hidden sm:block text-ink-muted text-lg pb-3">–</span>
              <div className="flex-1">
                <label className="eyebrow text-[10px] mb-2 block">Max</label>
                <div className="relative">
                  <input
                    type="number"
                    value={maxValue}
                    onChange={(e) => onMaxChange(e.target.value)}
                    placeholder={maxPlaceholder}
                    min="0"
                    className="w-full h-13 px-4 pr-10 rounded-xl border border-border bg-surface-cream text-base text-ink focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-ink-muted font-semibold pointer-events-none">
                    {unit}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SuggestionsDropdown({
  cities,
  suggestions,
  query,
  loading,
  focusedIdx,
  setFocusedIdx,
  onPickCity,
  onPickProperty,
  onSearchAll,
  isRent,
}: {
  cities: any[];
  suggestions: any[];
  query: string;
  loading: boolean;
  focusedIdx: number;
  setFocusedIdx: (i: number) => void;
  onPickCity: (name: string) => void;
  onPickProperty: (slug: string) => void;
  onSearchAll: () => void;
  isRent: boolean;
}) {
  const showEmpty = !loading && cities.length === 0 && suggestions.length === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.2, ease: EASE_PREMIUM }}
      role="listbox"
      className="absolute left-0 right-0 top-full mt-2 bg-surface rounded-2xl shadow-card-hover border border-border-subtle overflow-hidden z-50"
    >
      {loading && cities.length === 0 && suggestions.length === 0 && (
        <div className="px-4 py-3 space-y-2.5">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex items-center gap-3 animate-pulse">
              <div className="w-9 h-9 rounded-xl bg-surface-cream shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-surface-cream rounded w-2/3" />
                <div className="h-2.5 bg-surface-cream rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {cities.length > 0 && (
        <div>
          <p className="px-4 pt-3 pb-1.5 eyebrow flex items-center gap-1.5">
            <MapPin className="h-3 w-3" /> Villes &amp; régions
          </p>
          {cities.map((city: any, i: number) => {
            const focused = focusedIdx === i;
            return (
              <button
                key={city.id}
                type="button"
                onMouseDown={() => onPickCity(city.name)}
                onMouseEnter={() => setFocusedIdx(i)}
                role="option"
                aria-selected={focused}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                  focused ? 'bg-accent/8' : 'hover:bg-surface-cream',
                )}
              >
                <div
                  className={cn(
                    'w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors',
                    focused ? 'bg-accent/20 text-accent' : 'bg-surface-cream text-ink-muted',
                  )}
                >
                  <MapPin className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink">
                    <Highlight text={city.name} query={query} />
                  </p>
                </div>
                {city.region?.name && (
                  <span className="text-[10px] font-medium text-ink-muted bg-surface-cream px-2 py-0.5 rounded-full shrink-0">
                    {city.region.name}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {suggestions.length > 0 && (
        <div className={cn(cities.length > 0 && 'border-t border-border-subtle')}>
          <p className="px-4 pt-3 pb-1.5 eyebrow flex items-center gap-1.5">
            <Search className="h-3 w-3" /> Annonces correspondantes
          </p>
          {suggestions.map((prop: any, i: number) => {
            const idx = cities.length + i;
            const focused = focusedIdx === idx;
            return (
              <button
                key={prop.id}
                type="button"
                onMouseDown={() => onPickProperty(prop.slug)}
                onMouseEnter={() => setFocusedIdx(idx)}
                role="option"
                aria-selected={focused}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                  focused ? 'bg-accent/8' : 'hover:bg-surface-cream',
                )}
              >
                <div
                  className={cn(
                    'w-9 h-9 rounded-xl flex items-center justify-center shrink-0',
                    focused ? 'bg-accent/20 text-accent' : 'bg-primary/10 text-primary',
                  )}
                >
                  {PROP_TYPE_ICONS[prop.type] ?? <Home className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">
                    <Highlight text={prop.title} query={query} />
                  </p>
                  <p className="text-xs text-ink-muted mt-0.5 flex items-center gap-1">
                    <MapPin className="h-3 w-3 shrink-0" />
                    {prop.city?.name || 'Maroc'}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-medium text-ink">{formatPrice(Number(prop.price))}</p>
                  {isRent && <p className="text-[10px] text-ink-muted">/mois</p>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showEmpty && (
        <div className="px-4 py-6 text-center">
          <p className="text-sm text-ink-muted">
            Aucun résultat pour «&nbsp;{query}&nbsp;»
          </p>
          <button
            type="button"
            onMouseDown={onSearchAll}
            className="mt-2 text-xs font-medium text-accent hover:underline"
          >
            Lancer la recherche quand même
          </button>
        </div>
      )}

      {(cities.length > 0 || suggestions.length > 0) && (
        <button
          type="button"
          onMouseDown={onSearchAll}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 text-xs font-medium text-accent border-t border-border-subtle hover:bg-accent/5 transition-colors"
        >
          <Search className="h-3.5 w-3.5" />
          Voir tous les résultats pour «&nbsp;{query}&nbsp;»
        </button>
      )}
    </motion.div>
  );
}

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query || query.length < 2) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span className="font-semibold text-accent">{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
}
